<template>
    <v-dialog
        v-model="dialogModel"
        width="900"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        Upload a Profile Image
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="profileImageForm" lazy-validation>
                    <p class="mb-6" style="border-bottom: 1px solid #e0e0e0; padding-bottom: 32px;">
                        Please upload a professional photograph to be used on our website.
                        Please note that this image will be visible to our patients - so please don't send any pictures where you're at the club on a Saturday night!
                        Images should be taken from the mid chest/shoulders up and clearly show your face.
                        Clothing should not contain graphic material or branding from other dental companies you may have previously worked for.
                        Please see the attached example for guidance.
                    </p>

                    <img :src="require('@/assets/example-photo.webp')" alt="Profile Image Example" class="mx-auto d-block mb-4">

                    <drag-drop-file-uploader
                        v-model="documents"
                        :accept="fileAcceptScannedDocuments"
                        hint="Submit after you uploaded your file."
                        :error="submitted && !documents.length"
                    />

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                :rules="firstNameRules"
                                hint="First Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                :rules="lastNameRules"
                                hint="Last Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import DragDropFileUploader from '@/components/DragDropFileUploader.vue'
    import validationRules from '@/common/validationRules'
    import { mapActions } from 'vuex'
    import { FILE_ACCEPT_SCANNED_DOCUMENTS } from '@/common/constants'

    const emptyForm = () => ({
        firstName: '',
        lastName: ''
    })

    export default {
        name: 'ProfileImageFormDialog',
        components: {
            DragDropFileUploader
        },
        props: ['isOpen', 'programme'],
        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            documents: [],
            submitted: false,
            fileAcceptScannedDocuments: FILE_ACCEPT_SCANNED_DOCUMENTS,

            requiredRules: validationRules.required,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName
        }),
        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            }
        },
        methods: {
            ...mapActions([
             'showAlert',
             'uploadProfileImageForm'
            ]),
            buildPayload () {
                return {
                    transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                    firstName: this.form.firstName,
                    lastName: this.form.lastName,
                    formType: 'ProfileImage'
                }
            },
            resetForm () {
                this.form = emptyForm()
                this.documents = []
                this.submitted = false
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.profileImageForm
                this.submitted = true
                if (!formRef.validate() || !this.documents.length) {
                    this.showAlert({ text: 'Fill all the required fields please', type: 'error' })
                    return
                }

                this.submitLoading = true
                try {
                    const payload = this.buildPayload()
                    await this.uploadProfileImageForm(payload)
                    this.$emit('saved-with-additional-files', this.documents)
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
